import { AiOutlineWarning } from "react-icons/ai";
import ToggleSwitch from "../../components/ToggleSwitch";
import MaintenanceTitle from "../../components/MaintenanceSettingsInputs/MaintenanceTitle";
import MaintenanceMessage from "../../components/MaintenanceSettingsInputs/MaintenanceMessage";
import { Formik, Form, Field, ErrorMessage } from "formik";
import Errmsg from "../../components/ErrorMessage";

import * as Yup from "yup";
import toast from "react-hot-toast";
import axios from "axios";
import { useSelector } from "react-redux";
import { useEffect, useState } from "react";

import { Skeleton } from "@/components/ui/skeleton";
import { useTranslation } from "react-i18next";
import i18n from "@/i18n";

interface MaintenanceInitialValues {
  enabled: boolean;
  titleAR: string;
  titleEN: string;
  descriptionAR: string;
  descriptionEN: string;
}

function Maintenance() {
  const { t } = useTranslation();
  const lang = i18n.language;

  const [needRefetch, setNeedRefetch] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<null | string>(null);
  const [maintenanceInitialValues, setMaintenanceInitialValues] =
    useState<MaintenanceInitialValues>({
      enabled: false,
      titleAR: "",
      titleEN: "",
      descriptionAR: "",
      descriptionEN: "",
    });

  const token = useSelector(
    (state: { auth: { token: string } }) => state.auth.token,
  );
  const BASE_URL = import.meta.env.VITE_BASE_URL;

  useEffect(() => {
    if (!token) return

    const fetchMaintenance = async () => {
      const url = `${BASE_URL}/api/dashboard/settings/maintenance?locale=${lang}`;
      setNeedRefetch(false);
      setError(null);
      setIsLoading(true);
      try {
        const response = await axios.get(url, {
          headers: {
            Authorization: `${token}`,
          },
        });

        if (!response.data.success) {
          toast.error(
            t(
              "maintenanceSettings.fetchError",
            ) as string,
          );
          return;
        }

        setMaintenanceInitialValues({
          enabled: response.data?.enabled || false,
          titleAR: response.data?.titleAR || "",
          titleEN: response.data?.titleEN || "",
          descriptionAR: response.data?.descriptionAR || "",
          descriptionEN: response.data?.descriptionEN || "",
        });
      } catch (error: unknown) {
        if (axios.isAxiosError(error)) {
          const message =
            error.response?.data?.error ||
            (t(
              "maintenanceSettings.fetchError",
            ) as string);
          setError(message);
          toast.error(message);
        } else {
          setError(
            t(
              "maintenanceSettings.unexpectedError",
            ) as string,
          );
          toast.error(
            t(
              "maintenanceSettings.unexpectedError",
            ) as string,
          );
        }
      } finally {
        setIsLoading(false);
      }
    };

    fetchMaintenance();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lang, needRefetch, token]);

  if (isLoading)
    return (
      <>
        <div className="flex w-[80%] flex-col gap-5">
          <Skeleton className="h-[100px] w-full dark:bg-slate-600 dark:bg-opacity-35" />
          <Skeleton className="h-[80px] w-full dark:bg-slate-600 dark:bg-opacity-35" />
          <Skeleton className="h-[80px] w-full dark:bg-slate-600 dark:bg-opacity-35" />
          <Skeleton className="h-[80px] w-full dark:bg-slate-600 dark:bg-opacity-35" />
          <Skeleton className="h-[50px] w-[80px] self-end dark:bg-slate-600 dark:bg-opacity-35" />
        </div>
      </>
    );
  if (error) return <Errmsg msg={error} />;

  const validationSchema = Yup.object().shape({
    maintenanceTitleAr: Yup.string()
      .min(
        3,
        t(
          "maintenanceSettings.validation.arabicTitleMin",
        ) as string,
      )
      .max(
        100,
        t(
          "maintenanceSettings.validation.arabicTitleMax",
        ) as string,
      ),
    maintenanceTitleEn: Yup.string()
      .min(
        3,
        t(
          "maintenanceSettings.validation.englishTitleMin",
        ) as string,
      )
      .max(
        100,
        t(
          "maintenanceSettings.validation.englishTitleMax",
        ) as string,
      )
      .test(
        "at-least-one-title",
        t(
          "maintenanceSettings.validation.oneTitleRequired",
        ) as string,
        function (value) {
          const { maintenanceTitleAr } = this.parent;
          return Boolean(value?.trim() || maintenanceTitleAr?.trim());
        },
      ),

    maintenanceMessageAr: Yup.string()

      .min(
        3,
        t(
          "maintenanceSettings.validation.arabicMessageMin",
        ) as string,
      )
      .max(
        200,
        t(
          "maintenanceSettings.validation.arabicMessageMax",
        ) as string,
      ),
    maintenanceMessageEn: Yup.string()
    .min(
      3,
      t("maintenanceSettings.validation.englishMessageMin") as string,
    )
    .max(
      200,
      t("maintenanceSettings.validation.englishMessageMax") as string,
    )
    .test(
      "at-least-one-message",
      t("maintenanceSettings.validation.oneMessageRequired") as string,
      function (value: string | undefined) {
        const { maintenanceMessageAr } = this.parent;
        return Boolean(value?.trim() || maintenanceMessageAr?.trim());
      }
    )
  });

  const initialValues = {
    enabled: maintenanceInitialValues.enabled,
    maintenanceTitleAr: maintenanceInitialValues.titleAR || "",
    maintenanceTitleEn: maintenanceInitialValues.titleEN || "",
    maintenanceMessageAr: maintenanceInitialValues.descriptionAR || "",
    maintenanceMessageEn: maintenanceInitialValues.descriptionEN || "",
  };

  const handleFormSubmit = (values: typeof initialValues) => {
    const newMaintenanceValues = {
      enabled: values.enabled,
      titleAR: values.maintenanceTitleAr,
      titleEN: values.maintenanceTitleEn,
      descriptionAR: values.maintenanceMessageAr,
      descriptionEN: values.maintenanceMessageEn,
    };

    const handleChangeMaintanenceMode = async () => {
      const BASE_URL = import.meta.env.VITE_BASE_URL;
      const url = `${BASE_URL}/api/dashboard/settings/maintenance?locale=${lang}`;

      const LoadingToastId = toast.loading(
        t("updating") as string,
      );

      try {
        const response = await axios.put(url, newMaintenanceValues, {
          headers: {
            Authorization: `${token}`,
          },
        });


        if (!response.data.success) {
          toast.error(
            t(
              "maintenanceSettings.FupdateError"
            ) as string,
          );
          return;
        }

        toast.success(
          t(
            "maintenanceSettings.updateSuccess"
          ) as string,
        );

        setNeedRefetch(true);
      } catch (error: unknown) {
        if (axios.isAxiosError(error)) {
          const message =
            error.response?.data?.error ||
            (t(
              "maintenanceSettings.updateError"
            ) as string);
          toast.error(message);
        } else {
          console.error(error);
        }
      } finally {
        toast.dismiss(LoadingToastId);
      }
    };

    handleChangeMaintanenceMode();
  };

  async function PostForGetPreviewUrl() {
    const url = `${BASE_URL}/api/dashboard/menu/request-preview?locale=${lang}`;
    try {
      const response = await axios.post(
        url,
        {},
        {
          headers: {
            Authorization: `${token}`,
          },
        },
      );

      if (!response.data.success) {
        toast.error(
          t(
            "maintenanceSettings.fetchError"
          ) as string,
        );
        return;
      }


      window.open(response.data.previewUrl, "_blank");
    } catch (error) {
      if (axios.isAxiosError(error)) {
        const message =
          error.response?.data?.error.content ||
          (t(
            "maintenanceSettings.fetchError"
          ) as string);
        toast.error(message);
      } else {
        toast.error(
          t(
            "maintenanceSettings.unexpectedError"
          ) as string,
        );
      }
    }
  }

  return (
    <div className="w-full px-2 md:px-0 lg:w-[80%]">
      <h2 className="py-1 text-2xl font-semibold capitalize">
        {t("maintenanceSettings.mode")}
      </h2>

      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleFormSubmit}
      >
        {({ values, setFieldValue }) => (
          <Form className="mt-3 flex flex-col gap-4">
            {/* Warning */}
            <h1 className="flex flex-col items-center gap-4 rounded-md bg-yellow-400 p-6 text-base font-semibold text-yellow-900 sm:flex-row">
              <AiOutlineWarning className="flex-shrink-0 text-2xl" />
              <span>
                {t(
                  "maintenanceSettings.description",
                )}
              </span>
            </h1>

            {/* Toggle with label */}
            <ToggleSwitch
              checked={values.enabled}
              onChange={(checked) => setFieldValue("enabled", checked)}
              label={t("maintenanceSettings.messageColon") as string}
            />

            {/* Maintenance Title */}
            <div className="space-y-2">
              <label>{t("maintenanceSettings.titleColon")}</label>
              <Field name="maintenanceTitleAr">
                {() => (
                  <MaintenanceTitle
                    maintenanceTitles={{
                      ar: values.maintenanceTitleAr,
                      en: values.maintenanceTitleEn,
                    }}
                    onChange={(newTitle) => {
                      setFieldValue("maintenanceTitleAr", newTitle.ar);
                      setFieldValue("maintenanceTitleEn", newTitle.en);
                    }}
                  />
                )}
              </Field>
              <div className="space-y-1 text-sm text-red-500">
                <ErrorMessage name="maintenanceTitleAr" component="div" />
                <ErrorMessage name="maintenanceTitleEn" component="div" />
              </div>
            </div>

            {/* Maintenance Message */}
            <div className="space-y-2">
              <label>{t("maintenanceSettings.messageColon")}</label>
              <Field name="maintenanceMessageAr">
                {() => (
                  <MaintenanceMessage
                    maintenanceMessages={{
                      ar: values.maintenanceMessageAr,
                      en: values.maintenanceMessageEn,
                    }}
                    onChange={(newMessage) => {
                      setFieldValue("maintenanceMessageAr", newMessage.ar);
                      setFieldValue("maintenanceMessageEn", newMessage.en);
                    }}
                  />
                )}
              </Field>
              <div className="space-y-1 text-sm text-red-500">
                <ErrorMessage name="maintenanceMessageAr" component="div" />
                <ErrorMessage name="maintenanceMessageEn" component="div" />
              </div>
            </div>

            {/* Save button */}
            <div className="flex items-center justify-between">
              {initialValues.enabled && (
                <button
                  type="button"
                  onClick={PostForGetPreviewUrl}
                  className="mt-3 size-fit self-end rounded-md bg-sidebar px-4 py-2 font-semibold text-skyblue"
                >
                  {t("maintenanceSettings.previewMenu")}
                </button>
              )}

              <button
                className="mt-3 size-fit self-end rounded-md bg-sidebar px-4 py-2 font-semibold text-skyblue"
                type="submit"
              >
                {t("maintenanceSettings.save")}
              </button>
            </div>
          </Form>
        )}
      </Formik>
    </div>
  );
}

export default Maintenance;
